import { JWTService } from './jwtService';

/**
 * Facility CareDaily credential access.
 *
 * The plaintext API key is deliberately unavailable to the browser. It used to
 * be decrypted client-side via an RPC callable with the public anon key, which
 * meant anyone could retrieve any facility's key. All operations now go through
 * netlify/functions/facility-credentials, which derives the facility from the
 * caller's verified token and never returns the key itself.
 */

/** Metadata about a facility's stored credentials. Never includes the key. */
export interface CredentialStatus {
  hasKey: boolean;
  isSandbox: boolean;
  username: string | null;
  keyExpire: string | null;
  /** null when no expiry is known. */
  expired: boolean | null;
}

export interface LoginResponse {
  success: boolean;
  apiKey?: string;
  keyType?: number;
  keyExpire?: string;
  requiresPasscode?: boolean;
  passcodeDeliveryType?: number;
  passcodeDeliveryMethod?: string;
  sessionToken?: string;
  error?: string;
}

function endpoint(name: string): string {
  const explicitBase = process.env.REACT_APP_FUNCTIONS_BASE;
  if (explicitBase) return `${explicitBase.replace(/\/$/, '')}/${name}`;
  return `/.netlify/functions/${name}`;
}

function authHeaders(): Record<string, string> {
  const token = JWTService.getToken();
  if (!token) throw new Error('Not authenticated');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export class CredentialsService {
  /**
   * Metadata about the caller's facility credentials. The facility is taken
   * from the token server-side, so the argument is accepted only for call-site
   * compatibility and is not trusted.
   */
  static async getCredentialStatus(_facilityId?: string): Promise<CredentialStatus | null> {
    try {
      const response = await fetch(endpoint('facility-credentials') + '?action=status', {
        method: 'GET',
        headers: authHeaders(),
      });
      if (!response.ok) {
        console.error('❌ Credential status request failed:', response.status);
        return null;
      }
      return (await response.json()) as CredentialStatus;
    } catch (error) {
      console.error('❌ Failed to fetch credential status:', error);
      return null;
    }
  }

  /** Save an API key for the caller's facility. Requires STAFF. */
  static async saveApiKey(
    _facilityId: string,
    apiKey: string,
    isSandbox: boolean = false,
    username: string | null = null,
    keyExpire: string | null = null
  ): Promise<boolean> {
    try {
      const response = await fetch(endpoint('facility-credentials'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action: 'save', apiKey, isSandbox, username, keyExpire }),
      });
      if (!response.ok) {
        console.error('❌ Error saving credentials:', response.status);
        return false;
      }
      return true;
    } catch (error) {
      console.error('❌ Failed to save credentials:', error);
      return false;
    }
  }

  /** Delete the caller's facility credentials. Requires STAFF. */
  static async deleteCredentials(_facilityId: string): Promise<boolean> {
    try {
      const response = await fetch(endpoint('facility-credentials'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action: 'delete' }),
      });
      if (!response.ok) {
        console.error('❌ Error deleting credentials:', response.status);
        return false;
      }
      return true;
    } catch (error) {
      console.error('❌ Failed to delete credentials:', error);
      return false;
    }
  }

  /** Log in to CareDaily and store the resulting key server-side. */
  static async loginAndGenerateKey(
    facilityId: string,
    username: string,
    password: string,
    isSandbox: boolean = false,
    passcode: string | null = null,
    sessionToken: string | null = null
  ): Promise<LoginResponse> {
    try {
      const response = await fetch(endpoint('caredaily-login'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          username,
          password: passcode ? undefined : password,
          useSandbox: isSandbox,
          expiry: 36500,
          passcode,
          sessionToken,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result: LoginResponse = await response.json();

      if (result.success && result.apiKey) {
        await this.saveApiKey(facilityId, result.apiKey, isSandbox, username, result.keyExpire || null);
      }

      return result;
    } catch (error) {
      console.error('❌ Failed to login and generate key:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /** Human-readable summary of credential state for the admin panel. */
  static async testConnection(facilityId: string): Promise<{ success: boolean; message: string }> {
    const status = await this.getCredentialStatus(facilityId);

    if (!status) {
      return { success: false, message: 'Could not check credentials' };
    }
    if (!status.hasKey) {
      return { success: false, message: 'No credentials configured for this facility' };
    }

    const envLabel = status.isSandbox ? 'Sandbox' : 'Production';

    if (status.expired === true) {
      return { success: false, message: 'API key has expired. Please regenerate.' };
    }
    if (status.keyExpire) {
      const expires = new Date(status.keyExpire);
      return {
        success: true,
        message: `✅ Credentials configured for ${envLabel}. Key expires: ${expires.toLocaleDateString()}`,
      };
    }
    return { success: true, message: `✅ Credentials configured for ${envLabel} environment` };
  }

  static async hasCredentials(facilityId: string): Promise<boolean> {
    const status = await this.getCredentialStatus(facilityId);
    return status?.hasKey === true;
  }
}
